import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  InputAdornment,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { MoreHorizOutlined } from "@mui/icons-material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SearchOutlinedIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import Footer from "components/layout/Footer";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import ProfileAvatar from "components/base/ProfileAvatar";
import GroupAvatar from "features/Collections/components/GroupAvatar";
import ColumnsSection from "features/Collections/components/ColumnSection";
import { useIsTab } from "hooks/is-tab.hook";
import { fillParent, scrollingBox } from "utils/base-styles";
import { getRegularDate } from "utils/time.util";
import { useSelector } from "react-redux";
import { useLocalStorage } from "@rehooks/local-storage";
import { SUBMISSION_FOLDERS_UPDATED_EVENT, useTeacherSubmissionFolders } from "../hooks";
import {
  archiveSubmissionFolder,
  deleteSubmissionFolder,
  getSubmissionFolderDetail,
  removeSubmission,
  reviewSubmission,
  updateSubmissionFolder,
} from "../services";
import SubmissionFolderDialog from "./SubmissionFolderDialog";
import SubmissionFolderConfirmPopup from "./SubmissionFolderConfirmPopup";

const SUBMISSION_COLUMNS = [
  { field: "sources", headerName: "Sources" },
  { field: "submitted", headerName: "Submitted" },
  { field: "owner", headerName: "Owner" },
  { field: "reviewed", headerName: "Reviewed" },
  { field: "actions", headerName: "Actions" },
];

const DEFAULT_COLUMN_VISIBILITY = {
  sources: true,
  submitted: true,
  owner: true,
  reviewed: true,
  actions: true,
};

const SORT_OPTIONS = [
  { field: "collectionName_chr", label: "Collection" },
  { field: "sourceCount", label: "Sources" },
  { field: "submittedDateTime_dtm", label: "Submitted" },
  { field: "studentEmail_chr", label: "Owner" },
  { field: "isReviewed_ysn", label: "Reviewed" },
];

const ownerEmail = (row) =>
  row.studentEmail_chr || row.ownerEmail_chr || row.userName_chr || "—";

const ownerUserId = (row) =>
  row.studentUserID_chr || row.ownerUserID_chr || row.userId_chr || null;

const submissionOwners = (row) => {
  const candidates =
    row.owners || row.collectionOwners || row.sharedOwners || row.members || row.users;
  if (Array.isArray(candidates) && candidates.length) {
    return candidates.map((owner) =>
      typeof owner === "string"
        ? { email: owner }
        : {
            email:
              owner.studentEmail_chr || owner.ownerEmail_chr || owner.email_chr ||
              owner.email || owner.userName_chr || "—",
            userId:
              owner.studentUserID_chr || owner.ownerUserID_chr || owner.userId_chr ||
              owner.userID || owner.id || null,
            avatarUrl: owner.userAvatarUrl_chr || owner.avatarUrl || null,
          }
    );
  }

  const emails = row.ownerEmails || row.studentEmails || row.collectionOwnerEmails;
  if (Array.isArray(emails) && emails.length) {
    const userIds = row.ownerUserIds || row.studentUserIds || [];
    const avatarUrls = row.ownerAvatarUrls || row.studentAvatarUrls || [];
    return emails.map((email, index) => ({
      email,
      userId: userIds[index] || null,
      avatarUrl: avatarUrls[index] || null,
    }));
  }

  return [{
    email: ownerEmail(row),
    userId: ownerUserId(row),
    avatarUrl: row.userAvatarUrl_chr || null,
  }];
};

function SubmissionOwners({ row }) {
  const owners = submissionOwners(row);
  const primary = owners[0];
  return (
    <Stack direction="row" gap={1.5} alignItems="center" justifyContent="flex-end">
      {owners.length > 1 ? (
        <GroupAvatar
          userIds={owners.map((owner, index) => owner.userId || `submission-owner-${index}`)}
          userAvatars={owners.map((owner) => owner.avatarUrl)}
        />
      ) : (
        <ProfileAvatar userId={primary.userId} avatarUrl={primary.avatarUrl} />
      )}
      <Typography fontStyle="italic">
        {primary.email}{owners.length > 1 ? ` +${owners.length - 1} others` : ""}
      </Typography>
    </Stack>
  );
}

const sourceCount = (row) => row.sourceCount ?? row.totalSources ?? row.bookmarkCount ?? 0;

/** The four summary tiles above the submissions table. */
function StatTile({ label, value, loading }) {
  const theme = useTheme();
  return (
    <Stack
      gap={1}
      alignItems="center"
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 2.5,
        borderRadius: 1,
        backgroundColor: theme.palette.secondary.main,
      }}
    >
      <Typography variant="body2" color={theme.palette.secondary.contrastText} noWrap>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={90} height={24} />
      ) : (
        <Typography variant="body1" noWrap>{value}</Typography>
      )}
    </Stack>
  );
}

export default function SubmissionFolderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTab = useIsTab();
  const { user } = useSelector((state) => state.auth);
  const { folders, refresh: refreshFolders } = useTeacherSubmissionFolders();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [sortAnchor, setSortAnchor] = useState(null);
  const [sortCriteria, setSortCriteria] = useState("submittedDateTime_dtm");
  const [sortDirection, setSortDirection] = useState("asc");
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuRow, setRowMenuRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [unsubmitRow, setUnsubmitRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const columnStorageKey = `sooth-submission-columnvisibility-${user?.userId}`;
  const [storedVisibilityModel, setVisibilityModel] = useLocalStorage(
    columnStorageKey,
    DEFAULT_COLUMN_VISIBILITY
  );
  const visibilityModel = { ...DEFAULT_COLUMN_VISIBILITY, ...storedVisibilityModel };

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await getSubmissionFolderDetail(Number(id)));
    } catch (requestError) {
      toast.error(requestError.message || "Unable to load this submission folder.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // GetSubmissionFolderDetail omits list-level counters such as dlMemberCount,
  // so fold the folder-list entry in behind the detail payload.
  const folder = useMemo(() => {
    const listEntry = folders.find(
      (item) => Number(item.submissionFolderID_ids) === Number(id)
    );
    if (!listEntry && !detail) return null;
    return { ...(listEntry || {}), ...(detail || {}) };
  }, [detail, folders, id]);

  const submissions = useMemo(
    () => (Array.isArray(detail?.submissions) ? detail.submissions : []),
    [detail]
  );

  const totalSubmissions = folder?.totalSubmissions ?? submissions.length;
  const reviewedCount =
    folder?.reviewedCount ??
    submissions.filter((row) => row.isReviewed_ysn).length;

  const rows = useMemo(() => {
    const query = filter.toLowerCase().trim();
    const filtered = query
      ? submissions.filter((row) =>
          [row.collectionName_chr, ...submissionOwners(row).map((owner) => owner.email)].some((value) =>
            String(value || "").toLowerCase().includes(query)
          )
        )
      : [...submissions];

    const direction = sortDirection === "asc" ? 1 : -1;
    return filtered.sort((left, right) => {
      const leftValue =
        sortCriteria === "sourceCount" ? sourceCount(left) : left[sortCriteria];
      const rightValue =
        sortCriteria === "sourceCount" ? sourceCount(right) : right[sortCriteria];
      if (sortCriteria === "submittedDateTime_dtm") {
        return (new Date(leftValue || 0) - new Date(rightValue || 0)) * direction;
      }
      if (typeof leftValue === "number" || typeof leftValue === "boolean") {
        return (Number(leftValue || 0) - Number(rightValue || 0)) * direction;
      }
      return String(leftValue || "").localeCompare(String(rightValue || "")) * direction;
    });
  }, [filter, sortCriteria, sortDirection, submissions]);

  const isArchived = Boolean(folder?.isArchived_ysn);

  const notifyFolderChange = async () => {
    await refreshFolders().catch(() => {});
    window.dispatchEvent(new Event(SUBMISSION_FOLDERS_UPDATED_EVENT));
  };

  const setReviewed = async (row, isReviewed) => {
    try {
      const updated = await reviewSubmission({
        submissionID: row.submissionID_ids,
        isReviewed,
        teacherNote: row.teacherNote_chr || "",
      });
      setDetail((previous) => ({
        ...previous,
        submissions: previous.submissions.map((item) =>
          item.submissionID_ids === row.submissionID_ids
            ? { ...item, ...(updated || {}), isReviewed_ysn: isReviewed }
            : item
        ),
        reviewedCount:
          (previous.reviewedCount ?? 0) +
          (Boolean(row.isReviewed_ysn) === isReviewed ? 0 : isReviewed ? 1 : -1),
      }));
      notifyFolderChange();
    } catch (requestError) {
      toast.error(requestError.message || "Unable to update review status.");
    }
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuRow(null);
  };

  const unsubmit = async () => {
    if (!unsubmitRow) return;
    try {
      await removeSubmission(unsubmitRow.submissionID_ids);
      toast.success("Submission removed.");
      setUnsubmitRow(null);
      await loadDetail();
      notifyFolderChange();
    } catch (requestError) {
      toast.error(requestError.message || "Unable to remove submission.");
    }
  };

  const saveFolder = async (payload) => {
    setSaving(true);
    try {
      await updateSubmissionFolder({
        submissionFolderID: Number(id),
        folderName: payload.folderName,
        criteria: payload.criteria,
      });
      toast.success("Submission folder updated.");
      setEditOpen(false);
      await loadDetail();
      notifyFolderChange();
    } catch (requestError) {
      toast.error(requestError.message || "Unable to save submission folder.");
    } finally {
      setSaving(false);
    }
  };

  const archiveFolder = async () => {
    if (isArchived) return;
    try {
      await archiveSubmissionFolder(Number(id), true);
      toast.success("Submission folder archived.");
      setArchiveOpen(false);
      await loadDetail();
      notifyFolderChange();
    } catch (requestError) {
      toast.error(requestError.message || "Unable to update this submission folder.");
    }
  };

  const removeFolder = async () => {
    try {
      await deleteSubmissionFolder(Number(id));
      toast.success("Submission folder deleted.");
      setDeleteOpen(false);
      await notifyFolderChange();
      navigate("/collections/submissions", { replace: true });
    } catch (requestError) {
      toast.error(requestError.message || "Unable to delete submission folder.");
    }
  };

  const menuItemSx = {
    display: "flex",
    gap: 2,
    p: 1.5,
    width: "16rem",
    "&:hover": { backgroundColor: theme.palette.secondary.main },
  };

  return (
    <Box sx={{ ...fillParent, ...scrollingBox, px: { xs: 2, md: 4 } }}>
      <Helmet>
        <title>{`Sooth - Submissions${folder?.folderName_chr ? ` - ${folder.folderName_chr}` : ""}`}</title>
      </Helmet>
      <Stack gap={3} pb={8}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
          <Stack direction="row" gap={2} alignItems="center" sx={{ minWidth: 0 }}>
            {!folder ? (
              <Skeleton variant="text" width={isTab ? "60vw" : "30rem"} height={48} />
            ) : (
              <>
                <Typography
                  color="#5c6d7e"
                  variant="h3"
                  fontSize="3rem"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: { xs: "2.25rem", sm: "3.5rem" },
                    maxHeight: "7rem",
                    flexShrink: 1,
                  }}
                >
                  {folder.folderName_chr}
                </Typography>
                <AccessibleIconButton
                  title="Submission Folder Menu"
                  sx={{ p: 0, height: 18, width: 18 }}
                  onClick={(event) => setFolderMenuAnchor(event.currentTarget)}
                >
                  <MoreHorizOutlined />
                </AccessibleIconButton>
              </>
            )}
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            {showSearch ? (
              <TextField
                value={filter}
                autoFocus
                onBlur={() => !filter && setShowSearch(false)}
                onChange={(event) => setFilter(event.target.value)}
                label="Search Submissions"
                placeholder="Search submissions..."
                sx={{
                  "& legend": { backgroundColor: "transparent !important", position: "absolute" },
                }}
                slotProps={{
                  inputLabel: { sx: visuallyHidden },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon sx={{ height: 30, color: theme.palette.primary.dark }} />
                      </InputAdornment>
                    ),
                    endAdornment: filter && (
                      <InputAdornment position="end">
                        <AccessibleIconButton title="Clear Search" onClick={() => setFilter("")}>
                          <ClearIcon />
                        </AccessibleIconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            ) : (
              <AccessibleIconButton
                title="Search Submissions"
                sx={{ p: 0 }}
                onClick={() => setShowSearch(true)}
              >
                <SearchOutlinedIcon sx={{ color: theme.palette.primary.dark }} />
              </AccessibleIconButton>
            )}
            <AccessibleIconButton
              title="Sort Submissions"
              sx={{ p: 0 }}
              onClick={(event) => setSortAnchor(event.currentTarget)}
            >
              <SwapVertIcon sx={{ color: theme.palette.primary.dark }} />
            </AccessibleIconButton>
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <StatTile
            label="Date Created"
            loading={!folder}
            value={getRegularDate(folder?.createdDateTime_dtm)}
          />
          <StatTile
            label="Distribution List"
            loading={!folder}
            value={
              folder?.generatedDistributionName_chr ||
              folder?.distributionName_chr ||
              "—"
            }
          />
          <StatTile
            label="Submissions/Users"
            loading={!folder}
            value={`${totalSubmissions} / ${folder?.dlMemberCount ?? 0}`}
          />
          <StatTile
            label="Reviewed"
            loading={!folder}
            value={`${reviewedCount} / ${totalSubmissions}`}
          />
        </Stack>

        {folder?.criteria_chr && (
          <Stack direction="row" gap={1.5} alignItems="baseline">
            <Typography
              variant="h6"
              fontStyle="italic"
              color="#5c6d7e"
              sx={{ flexShrink: 0, lineHeight: 1.5 }}
            >
              Submission Criteria:
            </Typography>
            <Typography variant="body1" sx={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
              {folder.criteria_chr}
            </Typography>
          </Stack>
        )}

        <Box>
          <ColumnsSection
            visibilityModel={visibilityModel}
            setVisibilityModel={setVisibilityModel}
            columns={SUBMISSION_COLUMNS}
            label={columnStorageKey}
          />
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Collection</TableCell>
                  {visibilityModel.sources && <TableCell>Sources</TableCell>}
                  {visibilityModel.submitted && <TableCell>Submitted</TableCell>}
                  {visibilityModel.owner && <TableCell align="right">Owner</TableCell>}
                  {visibilityModel.reviewed && <TableCell align="center">Reviewed</TableCell>}
                  {visibilityModel.actions && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.submissionID_ids} hover>
                    <TableCell>
                      <Typography
                        component="span"
                        onClick={() =>
                          navigate(
                            `/collections/${row.collectionID_lng}/teacher-submission/${row.submissionID_ids}`,
                            { state: { submission: row, submissionFolderID: Number(id) } }
                          )
                        }
                        sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      >
                        {row.collectionName_chr || "Untitled collection"}
                      </Typography>
                    </TableCell>
                    {visibilityModel.sources && <TableCell>{sourceCount(row)}</TableCell>}
                    {visibilityModel.submitted && (
                      <TableCell>{getRegularDate(row.submittedDateTime_dtm)}</TableCell>
                    )}
                    {visibilityModel.owner && (
                      <TableCell align="right">
                        <SubmissionOwners row={row} />
                      </TableCell>
                    )}
                    {visibilityModel.reviewed && (
                      <TableCell align="center">
                        <Checkbox
                          inputProps={{ "aria-label": `Mark ${row.collectionName_chr} reviewed` }}
                          disabled={isArchived}
                          checked={Boolean(row.isReviewed_ysn)}
                          onChange={(event) => setReviewed(row, event.target.checked)}
                        />
                      </TableCell>
                    )}
                    {visibilityModel.actions && (
                      <TableCell align="center">
                        <AccessibleIconButton
                          title={`Actions for ${row.collectionName_chr || "submission"}`}
                          disabled={isArchived}
                          onClick={(event) => {
                            setRowMenuAnchor(event.currentTarget);
                            setRowMenuRow(row);
                          }}
                        >
                          <MoreHorizOutlined />
                        </AccessibleIconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {loading && <Typography color="text.secondary" mt={2}>Loading submissions…</Typography>}
          {!loading && rows.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {filter
                ? "No submissions match this search."
                : "No collections have been submitted to this folder yet."}
            </Alert>
          )}
        </Box>
      </Stack>
      <Footer sx={{ px: 0 }} />

      <Popover
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={() => setFolderMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableScrollLock
      >
        {!isArchived && (
          <>
            <MenuItem
              sx={{ ...menuItemSx, mt: 2 }}
              onClick={() => { setFolderMenuAnchor(null); setEditOpen(true); }}
            >
              <ModeEditOutlineOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              <Typography>Edit</Typography>
            </MenuItem>
            <MenuItem
              sx={menuItemSx}
              onClick={() => { setFolderMenuAnchor(null); setArchiveOpen(true); }}
            >
              <ArchiveOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              <Typography>Archive</Typography>
            </MenuItem>
          </>
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem
          sx={{ ...menuItemSx, mb: 2 }}
          onClick={() => { setFolderMenuAnchor(null); setDeleteOpen(true); }}
        >
          <DeleteOutlineIcon sx={{ color: theme.palette.primary.main }} />
          <Typography>Delete</Typography>
        </MenuItem>
      </Popover>

      <Popover
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableScrollLock
      >
        <MenuItem
          sx={{ ...menuItemSx, my: 1 }}
          onClick={() => { setUnsubmitRow(rowMenuRow); closeRowMenu(); }}
        >
          <RemoveCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
          <Typography>Unsubmit</Typography>
        </MenuItem>
      </Popover>

      <Popover
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.field}
            selected={sortCriteria === option.field}
            sx={menuItemSx}
            onClick={() => {
              setSortDirection(
                sortCriteria === option.field && sortDirection === "asc" ? "desc" : "asc"
              );
              setSortCriteria(option.field);
              setSortAnchor(null);
            }}
          >
            <Typography>{option.label}</Typography>
            {sortCriteria === option.field && (
              <Typography color="text.secondary" sx={{ ml: "auto" }}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Popover>

      <SubmissionFolderConfirmPopup
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={archiveFolder}
        title="Confirm Archive"
        confirmLabel="Archive"
      >
        Are you sure you want to archive “{folder?.folderName_chr}”? Students will no longer
        be able to submit to it, and existing submissions will become permanently read-only.
      </SubmissionFolderConfirmPopup>

      <SubmissionFolderConfirmPopup
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={removeFolder}
        title="Confirm Delete"
        confirmLabel="Delete"
      >
        Are you sure you want to delete “{folder?.folderName_chr}”? Any collections submitted to
        this folder will be unsubmitted. The students&apos; collections themselves will not be
        deleted. This cannot be undone.
      </SubmissionFolderConfirmPopup>

      <SubmissionFolderConfirmPopup
        open={Boolean(unsubmitRow)}
        onClose={() => setUnsubmitRow(null)}
        onConfirm={unsubmit}
        title="Confirm Unsubmit"
        confirmLabel="Unsubmit"
      >
        Are you sure you want to remove “{unsubmitRow?.collectionName_chr}” from this folder? The
        student&apos;s collection will not be deleted, and they will be able to submit it again.
      </SubmissionFolderConfirmPopup>

      <SubmissionFolderDialog
        open={editOpen}
        folder={folder}
        saving={saving}
        onClose={() => setEditOpen(false)}
        onSubmit={saveFolder}
      />
    </Box>
  );
}
